import { ThumbsUpIcon } from '@m/alchemy-ui/icons/ThumbsUpIcon';
import { StarFilledIcon } from '@m/alchemy-ui/icons/StarFilledIcon';
import { useIntl } from 'react-intl';
import { VotingType, type VotingType as VotingTypeEnum } from '../../../common/core-api';
import { useRuntimeCommunity } from '../../../context';
import { getDisplayVoteValue, getVoteLabel } from '../IdeaCard.helpers';
import * as Styled from './VoteActions.styled';
import * as CardStyled from './IdeaCard.styled';

interface IdeaVoteBoxProps {
  readonly voteCount: number | null;
  readonly score?: number | null;
  readonly votingType?: VotingTypeEnum | null;
  readonly isBlindVoting?: boolean;
  readonly canModerate?: boolean;
  readonly labelOverride?: string;
  readonly showThumbsUp?: boolean;
  readonly maxStars?: number;
}

export const IdeaVoteBox = ({
  voteCount,
  score,
  votingType,
  isBlindVoting = false,
  canModerate = false,
  labelOverride,
  showThumbsUp = false,
  maxStars = 3
}: IdeaVoteBoxProps) => {
  const { colors } = useRuntimeCommunity();
  const intl = useIntl();
  const voteBarColor = colors?.voteBar ? `#${colors.voteBar}` : '#5c5c5c';
  const buttonColor = colors?.button ? `#${colors.button}` : '#f5a623';

  // Hide vote count
  if (isBlindVoting && !canModerate) return null;

  // STARS display
  if (votingType === VotingType.STARS) {
    const avgScore = score ?? 0;
    const ratingCount = voteCount ?? 0;
    const ratingLabel = intl.formatMessage(
      { id: 'ratingCount', defaultMessage: '{count, plural, one {Rating} other {Ratings}}' },
      { count: ratingCount }
    );

    return (
      <Styled.StarDisplayWrap $bgColor={voteBarColor}>
        <Styled.StarDisplayRow>
          {avgScore > 0 ? (
            Array.from({ length: maxStars }, (_, i) => {
              const starNum = i + 1;
              if (avgScore >= starNum) {
                // Full star
                return (
                  <Styled.StarDisplayActive key={starNum} $buttonColor={buttonColor}>
                    <StarFilledIcon aria-hidden="true" />
                  </Styled.StarDisplayActive>
                );
              }
              if (avgScore >= starNum - 0.5) {
                // Half star
                return (
                  <Styled.StarDisplayHalf key={starNum} $buttonColor={buttonColor}>
                    <StarFilledIcon aria-hidden="true" />
                    <StarFilledIcon aria-hidden="true" />
                  </Styled.StarDisplayHalf>
                );
              }
              // Empty star
              return (
                <Styled.StarDisplayInactive key={starNum}>
                  <StarFilledIcon aria-hidden="true" />
                </Styled.StarDisplayInactive>
              );
            })
          ) : (
            <span>{String('n/a')}</span>
          )}
        </Styled.StarDisplayRow>
        <Styled.StarRatingCount>
          {ratingCount} {ratingLabel}
        </Styled.StarRatingCount>
      </Styled.StarDisplayWrap>
    );
  }

  const label = labelOverride ?? getVoteLabel(votingType);
  const displayValue = getDisplayVoteValue(voteCount, score ?? null);

  return (
    <CardStyled.VoteCount $bgColor={voteBarColor}>
      {showThumbsUp && (
        <CardStyled.ThumbsUpIconWrap>
          <ThumbsUpIcon aria-hidden="true" />
        </CardStyled.ThumbsUpIconWrap>
      )}
      <CardStyled.VoteValue>{displayValue}</CardStyled.VoteValue>
      <span>{label}</span>
    </CardStyled.VoteCount>
  );
};
